/**
 * Two facts about this browser tab that must survive a full-page redirect to
 * Keycloak and back.
 *
 * `sessionStorage`, not `localStorage`: both are scoped to one tab's lifetime
 * on purpose. A sign-out in one tab must not silently sign the app out in
 * another, and a parked destination must not outlive the tab that parked it.
 */

const SIGNED_OUT = 'internal.signedOut'
const RETURN_TO = 'internal.returnTo'

/** Reading storage can throw — a locked-down browser, a sandboxed frame. */
function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key)
    else window.sessionStorage.setItem(key, value)
  } catch {
    /* A tab that cannot remember simply behaves as it did before. */
  }
}

/**
 * The user pressed «Cerrar sesión».
 *
 * ── WITHOUT THIS FLAG, SIGN-OUT DOES NOT WORK ───────────────────────────────
 * `logout()` returns to the app's origin, `check-sso` finds no session, and
 * `StaffOnly`'s effect immediately redirects to Keycloak — which, with a live
 * SSO session elsewhere, signs you straight back in. Sign-out that instantly
 * bounces you back reads as the app refusing to let go.
 */
export const signedOut = {
  mark: () => write(SIGNED_OUT, '1'),
  clear: () => write(SIGNED_OUT, null),
  is: () => read(SIGNED_OUT) === '1',
}

/**
 * Where the user was going before being sent to the login page.
 *
 * `AuthProvider` pins `redirectUri` to the app's ORIGIN, deliberately — reading
 * `window.location.href` at call time is what produced "Invalid parameter:
 * redirect_uri" — so Keycloak always returns to `/`. Without parking the
 * destination, every deep link a logged-out recipient opens lands on Resumen,
 * which directly contradicts the one thing this tool is for.
 */
export const returnTo = {
  park: (pathAndQuery: string) => {
    // Only in-app paths, and never the root: restoring `/` is a no-op that
    // would just add a history entry. A value that does not start with a single
    // `/` could be an absolute URL to somewhere else.
    if (pathAndQuery.startsWith('/') && !pathAndQuery.startsWith('//') && pathAndQuery !== '/') {
      write(RETURN_TO, pathAndQuery)
    }
  },
  take: (): string | null => {
    const v = read(RETURN_TO)
    write(RETURN_TO, null)
    return v && v.startsWith('/') && !v.startsWith('//') ? v : null
  },
}
